import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, AlertTriangle, Flame, MessageSquare } from "lucide-react";

const PRIO_COLORS = {
  normal: "border-slate-200 dark:border-slate-700",
  important: "border-amber-400 bg-amber-50/50 dark:bg-amber-950/20",
  urgent: "border-rose-500 bg-rose-50/50 dark:bg-rose-950/20",
};
const PRIO_ICON = { normal: MessageSquare, important: AlertTriangle, urgent: Flame };

export default function HandoverPage() {
  const { user, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", priority: "normal" });

  const load = async () => setItems((await api.get("/comms/handover")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/comms/handover", form);
      toast.success("Eingetragen");
      setDialog(false);
      setForm({ title: "", content: "", priority: "normal" });
      await load();
    } catch (err) { toast.error("Fehler"); }
  };

  const markRead = async (id) => { await api.post(`/comms/handover/${id}/read`); await load(); };
  const del = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/comms/handover/${id}`); await load(); };

  return (
    <div className="space-y-6" data-testid="handover-root">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Team</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Übergabebuch</h2>
        </div>
        <Button data-testid="handover-add-btn" onClick={() => setDialog(true)} className="rounded-full"><Plus className="w-4 h-4 mr-2" /> Neuer Eintrag</Button>
      </div>

      <div className="space-y-3">
        {items.map((h) => {
          const Icon = PRIO_ICON[h.priority] || MessageSquare;
          const readByMe = h.read_by?.includes(user.id);
          return (
            <Card key={h.id} data-testid={`handover-${h.id}`} className={`border-l-4 ${PRIO_COLORS[h.priority]} card-hover`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${h.priority === "urgent" ? "text-rose-600" : h.priority === "important" ? "text-amber-600" : "text-slate-500"}`} />
                    <h3 className="font-heading font-bold text-lg">{h.title}</h3>
                    {h.priority !== "normal" && <Badge variant="outline" className="uppercase text-[10px]">{h.priority}</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString("de-DE")}</div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{h.content}</p>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-border/60">
                  <div className="text-xs text-slate-500">{h.read_by?.length || 0} Person(en) gelesen</div>
                  <div className="flex gap-2">
                    {!readByMe && (
                      <Button data-testid={`handover-read-${h.id}`} size="sm" variant="outline" onClick={() => markRead(h.id)}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Verstanden
                      </Button>
                    )}
                    {readByMe && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Gelesen</Badge>}
                    {isManager && <Button size="icon" variant="ghost" onClick={() => del(h.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {items.length === 0 && <div className="text-center text-slate-500 py-12">Noch keine Einträge.</div>}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Neuer Übergabebuch-Eintrag</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium">Titel</label><Input data-testid="handover-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Inhalt</label><Textarea data-testid="handover-content" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            <div>
              <label className="text-sm font-medium">Priorität</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="important">Wichtig</SelectItem><SelectItem value="urgent">Dringend</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
            <Button data-testid="handover-save" onClick={create}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
